import React from 'react'

import { observer } from 'mobx-react'
import { useForm } from 'react-hook-form'
import moment from 'moment'

import { formatDate } from 'utils/formatters'
import { useStores } from 'stores'
import Button from 'common/Button'
import DatePicker from 'common/Forms/DatePicker'
import MessageList from 'common/MessageList'
import Modal from 'common/Modal'
import ReadOnlyField from 'common/ReadOnlyField'

export default observer(({ resource, commonResource, onClose }) => {
  const { authStore, reportStore } = useStores()
  const [loading, setLoading] = React.useState()
  const [error, setError] = React.useState()
  const downloadLinkRef = React.useRef()
  const methods = useForm()

  const { handleSubmit } = methods
  const session = authStore.getSession()

  const downloadFileName = React.useMemo(() => {
    const fileType = session.fileTypeConfig || 'xlsx'
    return `${resource.CasesReportFileName || 'CasesReport'}.${fileType}`
  }, [resource, session])

  const handleOnSubmit = async values => {
    try {
      setLoading(true)
      setError(null)

      const startDate = formatDate(values.startDate)
      const endDate = formatDate(values.endDate)
      const payload = {
        DateFrom: startDate,
        DateTo: endDate,
      }

      const { data, error } = await reportStore.downloadBlob('cases/mycasesorderline', payload, {
        ignoreGlobalMessage: true,
      })
      if (data && !error) {
        const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        downloadLinkRef.current.href = window.URL.createObjectURL(blob)
        downloadLinkRef.current.click()
        onClose()
      } else {
        setError(error)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen
      useForm={methods}
      title={resource.DownloadCasesReport || 'Download Cases Report'}
      disableCancel={loading}
      footer={
        <div className="w-100 d-flex">
          <Button text disabled={loading} className="me-2 px-4" onClick={onClose}>
            {commonResource.Cancel}
          </Button>
          <Button type="submit" color="primary" disabled={loading} loading={loading} className="ms-auto">
            {resource.Download}
          </Button>
        </div>
      }
      onClose={onClose}
      onSubmit={handleSubmit(handleOnSubmit)}
    >
      {loading && (
        <MessageList
          title={resource.Downloading || 'Downloading'}
          messages={[resource.DownloadingMessage || 'Please wait while your file is being generated.']}
          color="primary"
          hideClose
        />
      )}
      <MessageList messages={error && [error]} />
      <DatePicker
        name="startDate"
        label={resource.StartDate || 'Start Date'}
        defaultValue={moment().subtract(1, 'month').toDate()}
        formGroup
        horizontal
      />
      <DatePicker
        name="endDate"
        label={resource.EndDate || 'End Date'}
        defaultValue={moment().toDate()}
        formGroup
        horizontal
      />
      <ReadOnlyField
        value={resource.DateRangeInfo || 'Date range is based on the creation date of the case'}
        formGroup
        horizontal
        inputCol={{
          md: { offset: 3, size: 9 },
        }}
        formClassName="mb-0"
      />
      <a href="#" ref={downloadLinkRef} download={downloadFileName} className="d-none">
        download
      </a>
    </Modal>
  )
})
